import { classNames } from '@framework/utils';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TextAlign } from '@view-model/models/common/text-align';
import { forwardRef, useCallback, useImperativeHandle } from 'react';

type Props = {
  onChangeTextAlign(textAlign: TextAlign): void;
  onOpenSubMenu(menuType: string): void;
};

export const TextAlignMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onChangeTextAlign, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('textAlign');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.textAlign')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute -right-64 top-0 z-50 w-64 rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextAlign(TextAlign.Left)}
        >
          <div className="inline-block pl-2">{t('canvas.node.textAlign.left')}</div>
        </button>
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextAlign(TextAlign.Center)}
        >
          <div className="inline-block pl-2">{t('canvas.node.textAlign.center')}</div>
        </button>
        <button
          className={'flex w-full cursor-pointer px-8 py-4 hover:bg-gray-200 focus:outline-none'}
          onClick={() => onChangeTextAlign(TextAlign.Right)}
        >
          <div className="inline-block pl-2">{t('canvas.node.textAlign.right')}</div>
        </button>
      </div>
    </div>
  );
});

TextAlignMenuButton.displayName = 'TextAlignMenuButton';
